import {
  Button,
  Divider,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Switch,
  SwitchOnChangeData,
  Text,
} from "@fluentui/react-components";
import { Settings24Regular } from "@fluentui/react-icons";
import { EAppSettings, SelectItem } from "library/types";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AppSpinner } from "components/AppSpinner/AppSpinner";
import { Flex } from "components/styled";
import { ViewSelectionToggle } from "components/ViewSelectionToggle/ViewSelectionToggle";
import { useAppTheme } from "hooks/useAppTheme";
import { useGetUserAppTargetSetting } from "hooks/useGetUserAppTargetSetting";
import { APP_ID, APP_SETTING_ID } from "library/constants";
import { useAddAppSetting } from "state/queries/useAddAppSetting";
import { useUpdateAppSettings } from "state/queries/useUpdateAppSettings";
import { SelectLanguage } from "./SelectLanguage";
import { ThemeSettings } from "./ThemeSettings/ThemeSettings";
import { InlinePhraseToggle } from "./InlinePhraseToggle/InlinePhraseToggle";
import { useEffect, useState } from "react";
import { MSGraphAuth } from "service/GraphService";

type Props = {
  languageOptions: SelectItem[];
  onSelect: (
    event: React.FormEvent<HTMLDivElement>,
    option?: SelectItem,
    index?: number
  ) => void;
  selectedLanguage: string;
};

export function SettingsMenu({
  languageOptions,
  onSelect,
  selectedLanguage,
}: Props) {
  const theme = useAppTheme();
  const { t } = useTranslation();
  const [shouldShowPhrase, setShouldShowPhrase] = useState(false);

  const darkModeAppSetting = useGetUserAppTargetSetting({
    targetSettingName: EAppSettings.darkMode,
  });

  const { mutate: onUpdateAppSetting, isLoading: updateInProgress } =
    useUpdateAppSettings();
  const { mutate: onAddAppSetting, isLoading: addInProgress } =
    useAddAppSetting();

  const isLoading = updateInProgress || addInProgress;

  const enableInContextEditor = async () => {
    const account = await MSGraphAuth.accountObj;
    if (!(account && account.idTokenClaims)) return false;

    const roles: string[] = (account.idTokenClaims as { roles: string[] })
      .roles;

    return !!(roles && roles.includes("PHRASE"));
  };

  useEffect(() => {
    (async () => {
      setShouldShowPhrase(await enableInContextEditor());
    })();
  }, [setShouldShowPhrase]);

  return (
    <Container>
      {isLoading && <AppSpinner />}
      <Menu positioning={{ autoSize: true }} persistOnItemClick>
        <MenuTrigger disableButtonEnhancement>
          <Button
            size="large"
            appearance="transparent"
            icon={
              <div>
                <Settings24Regular />
              </div>
            }
            style={{
              color: theme.palette.purpleDark,
              minWidth: "fit-content",
            }}
          />
        </MenuTrigger>

        <MenuPopover
          style={{
            padding: 0,
          }}
        >
          <MenuList
            style={{
              padding: "10px 0",
              width: "255px",
            }}
          >
            <StyledMenuItem>
              <SelectLanguage
                languageOptions={languageOptions}
                onSelect={onSelect}
                selectedLanguage={selectedLanguage}
              />
            </StyledMenuItem>
            <Divider
              style={{
                padding: "5px 0",
              }}
            />
            <StyledMenuItem>
              <ThemeSettings />
            </StyledMenuItem>
            <Divider
              style={{
                padding: "5px 0",
              }}
            />
            <StyledMenuItem>
              <ViewSelectionToggle />
            </StyledMenuItem>
            {shouldShowPhrase && (
              <>
                <Divider
                  style={{
                    padding: "5px 0",
                  }}
                />
                <StyledMenuItem>
                  <InlinePhraseToggle />
                </StyledMenuItem>
              </>
            )}
            <Divider
              style={{
                padding: "5px 0",
              }}
            />
            <StyledMenuItem>
              <Flex width={"100%"} justify="space-between">
                <Text>{t("header.DarkMode")}</Text>
                <Switch
                  defaultChecked={
                    darkModeAppSetting?.userAppSettingValue === "true"
                  }
                  onChange={(_, data: SwitchOnChangeData) => {
                    if (darkModeAppSetting) {
                      onUpdateAppSetting({
                        payload: [
                          {
                            ...darkModeAppSetting,
                            userAppSettingValue: data.checked.toString(),
                          },
                        ] as any,
                      });
                    } else {
                      onAddAppSetting({
                        payload: [
                          {
                            applicationCodeId: APP_ID,
                            userAppSettingValue: data.checked.toString(),
                            appSettingCodeId: APP_SETTING_ID,
                            userAppSettingName: EAppSettings.darkMode,
                            isDefault: false,
                          },
                        ],
                      });
                    }
                  }}
                  indicator={{
                    style: {
                      margin: 0,
                    },
                  }}
                />
              </Flex>
            </StyledMenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    </Container>
  );
}

const Container = styled.div``;
const StyledMenuItem = styled(MenuItem)`
  padding: 7px 20px;
`;
